/**
 * Golorama — formulaire chromatique.
 *
 * `yeux` / `cheveux` / `peau` forment une clé qui adresse PALETTES : 45
 * combinaisons découpées en 3 tiers. Les cartes de collection sont
 * pré-rendues dans un pool caché ; le JS les DÉPLACE (`appendChild`) dans
 * la grille du tier, dans l'ordre de la palette. `genre` ne touche pas à
 * la palette : seulement le lien et le visuel des cartes (applyGender).
 *
 * ATTENTION — l'ordre dans PALETTES est significatif (il vient de l'Excel
 * client) mais ne doit JAMAIS être présenté comme un classement.
 *
 * Table PALETTES générée depuis l'onglet `Import` de Golorama_2026_2.xlsx.
 * Ne pas éditer à la main : régénérer depuis la source.
 */

(() => {
  const STORAGE_KEY = "golorama_state_v3";
  // Purge des schémas à codes numériques (« 213 »), sans équivalent ici.
  const LEGACY_STORAGE_KEYS = ["golorama_code_v2", "golorama_code"];
  const LEGACY_COOKIE_NAME = "code";

  // ===================================================================
  // Données
  // ===================================================================

  // Clé : "{yeux}|{cheveux}|{peau}".
  // Valeur : "tier1 / tier2 / tier3", slugs de couleur séparés par des
  // espaces. Les slugs correspondent aux `data-color` des cartes.
  const PALETTES = {
    "bleu|blond-chatain|claire": "bleu bordeaux blanc violet / vert beige rouge / marine gris blush noir",
    "bleu|blond-chatain|intermediaire": "bleu vert bordeaux marron / violet beige rouge / marine gris blush noir",
    "bleu|blond-chatain|foncee": "blanc bleu orange rouge / violet jaune vert / marine blush gris noir",
    "bleu|brun-marron|claire": "bleu bordeaux rouge blanc / violet vert beige / marine noir blush gris",
    "bleu|brun-marron|intermediaire": "bleu bordeaux vert marron / violet beige orange / marine noir blush gris",
    "bleu|brun-marron|foncee": "blanc bleu rouge violet / orange jaune vert / marine blush gris noir",
    "bleu|noir|claire": "rouge bleu bordeaux blanc / violet vert beige / marine noir blush gris",
    "bleu|noir|intermediaire": "bleu bordeaux vert rouge / violet blanc marron / marine noir blush gris",
    "bleu|noir|foncee": "blanc bleu rouge violet / orange jaune vert / marine blush gris noir",
    "bleu|roux|claire": "vert bleu bordeaux marron / violet beige rouge / marine gris blush noir",
    "bleu|roux|intermediaire": "vert bordeaux marron bleu / violet beige orange / marine gris blush noir",
    "bleu|roux|foncee": "vert blanc bleu rouge / violet orange jaune / marine blush gris noir",
    "bleu|blanc-gris|claire": "bleu violet bordeaux vert / rouge blanc beige / marine gris blush noir",
    "bleu|blanc-gris|intermediaire": "bleu violet vert bordeaux / rouge marron blanc / marine gris blush noir",
    "bleu|blanc-gris|foncee": "blanc bleu rouge violet / orange jaune vert / marine blush gris noir",

    "vert|blond-chatain|claire": "bordeaux violet vert bleu / beige rouge blanc / marine gris blush noir",
    "vert|blond-chatain|intermediaire": "bordeaux violet vert marron / bleu beige orange / marine gris blush noir",
    "vert|blond-chatain|foncee": "blanc bordeaux violet rouge / orange jaune vert / marine blush gris noir",
    "vert|brun-marron|claire": "bordeaux violet rouge vert / bleu blanc beige / marine noir blush gris",
    "vert|brun-marron|intermediaire": "bordeaux violet vert marron / bleu orange beige / marine noir blush gris",
    "vert|brun-marron|foncee": "blanc bordeaux violet rouge / orange jaune bleu / marine noir blush gris",
    "vert|noir|claire": "rouge bordeaux violet blanc / bleu vert beige / marine noir blush gris",
    "vert|noir|intermediaire": "bordeaux violet vert rouge / bleu blanc marron / marine noir blush gris",
    "vert|noir|foncee": "blanc rouge violet bordeaux / orange jaune bleu / marine noir blush gris",
    "vert|roux|claire": "vert bordeaux violet marron / bleu beige rouge / marine gris blush noir",
    "vert|roux|intermediaire": "vert bordeaux violet marron / bleu orange beige / marine gris blush noir",
    "vert|roux|foncee": "vert blanc bordeaux violet / rouge orange bleu / marine blush gris noir",
    "vert|blanc-gris|claire": "violet bordeaux bleu vert / rouge blanc beige / marine gris blush noir",
    "vert|blanc-gris|intermediaire": "violet bordeaux vert bleu / rouge marron blanc / marine gris blush noir",
    "vert|blanc-gris|foncee": "blanc violet bordeaux rouge / vert bleu orange / marine blush gris noir",

    "marron|blond-chatain|claire": "bleu bordeaux vert violet / beige rouge blanc / marine gris blush noir",
    "marron|blond-chatain|intermediaire": "bleu bordeaux vert marron / violet beige orange / marine gris blush noir",
    "marron|blond-chatain|foncee": "blanc bleu orange rouge / jaune violet vert / marine blush gris noir",
    "marron|brun-marron|claire": "bleu bordeaux vert violet / rouge blanc beige / marine noir blush gris",
    "marron|brun-marron|intermediaire": "bordeaux bleu vert marron / violet orange beige / marine noir blush gris",
    "marron|brun-marron|foncee": "blanc bleu rouge bordeaux / violet orange jaune / marine noir blush gris",
    "marron|noir|claire": "rouge bleu bordeaux blanc / vert violet beige / marine noir blush gris",
    "marron|noir|intermediaire": "bleu bordeaux rouge vert / violet blanc orange / marine noir blush gris",
    "marron|noir|foncee": "blanc rouge bleu violet / orange jaune bordeaux / marine noir blush gris",
    "marron|roux|claire": "vert bordeaux marron bleu / beige violet orange / marine gris blush noir",
    "marron|roux|intermediaire": "vert bordeaux marron orange / bleu beige violet / marine gris blush noir",
    "marron|roux|foncee": "vert blanc bordeaux rouge / bleu orange violet / marine blush gris noir",
    "marron|blanc-gris|claire": "bleu bordeaux violet vert / rouge blanc beige / marine gris blush noir",
    "marron|blanc-gris|intermediaire": "bleu bordeaux violet vert / rouge marron blanc / marine gris blush noir",
    "marron|blanc-gris|foncee": "blanc bleu violet rouge / bordeaux orange jaune / marine blush gris noir",
  };

  // Suffixe de la clé dataset par genre : data-url-femme -> dataset.urlFemme.
  const GENDER_SUFFIX = { femme: "Femme", homme: "Homme", na: "Na" };
  const DEFAULT_GENDER = "na";
  const TIER_COUNT = 3;

  const AJAX_VIEW = "golorama-ajax";
  const AJAX_TIMEOUT_MS = 4000;
  const AJAX_STORAGE_KEY = "golorama_availability_v1";
  // Clé "{slug}|{genre}" → true (des produits) | false (aucun).
  // Une clé absente signifie « pas encore su », jamais « vide ».
  const AVAILABILITY = new Map();

  // ===================================================================
  // Tracking GA4 : golorama_start > golorama_complete > golorama_click
  // GA4 est chargé par l'app Google & YouTube, qui pousse un `config` par
  // ID dans `window.dataLayer` une fois le consentement donné. Sans ce
  // `config` GA4, rien n'est envoyé. Renvoie `true` si l'événement est parti.
  // ===================================================================

  function track(name) {
    const layer = window.dataLayer;
    const config = Array.isArray(layer) && layer.find((e) => e && e[0] === "config" && /^G-/.test(e[1]));
    if (!config) return false;

    // Format gtag : l'objet `arguments` tel quel (gtag.js ignore un
    // tableau). `send_to` écarte Google Ads, configuré sur la même balise.
    (function () {
      layer.push(arguments);
    })("event", name, { send_to: config[1] });
    return true;
  }

  // ===================================================================
  // Tracking Klaviyo : metric « Golorama Completed » à la soumission,
  // seulement pour un profil déjà connu de Klaviyo. On remonte les
  // couleurs recommandées par tier, jamais les réponses ; la date est
  // l'horodatage de l'événement côté Klaviyo.
  // ===================================================================

  function trackKlaviyo(results) {
    const k = window.klaviyo;
    if (!k || typeof k.isIdentified !== "function") return;

    // Lu dans les grilles rendues : une couleur écartée faute de produit
    // pour le genre choisi ne doit pas être remontée.
    const props = {};
    for (let i = 1; i <= TIER_COUNT; i += 1) {
      const cards = results.querySelectorAll(`[data-golorama-tier-grid="${i}"] [data-color]`);
      props[`Tier ${i}`] = [...cards].map((card) => card.dataset.color);
    }

    // Ordre de la palette = ordre de l'Excel client : la première couleur
    // du tier 1 tient lieu de « meilleure couleur ». Libellé affiché de la
    // carte, pour être repris tel quel dans un email.
    const best = results.querySelector('[data-golorama-tier-grid="1"] [data-color]');
    const bestTitle = best && best.querySelector(".golorama_card_title");
    const bestColor = best ? (bestTitle && bestTitle.textContent.trim()) || best.dataset.color : null;
    props["Golorama Best Color"] = bestColor;

    // Sans consentement cookies, Klaviyo ne s'initialise pas et la
    // promesse ne se résout jamais : rien n'est envoyé.
    try {
      Promise.resolve(k.isIdentified())
        .then((known) => {
          if (!known) return;
          // Aussi en propriété de profil, pour `person|lookup` dans une
          // campagne qui n'est pas déclenchée par la metric.
          if (bestColor) k.identify({ "Golorama Best Color": bestColor });
          k.track("Golorama Completed", props);
        })
        .catch(() => {});
    } catch (e) {}
  }

  // ===================================================================
  // Init
  // ===================================================================

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  function init() {
    const section = document.querySelector("[data-golorama]");
    if (!section) return;

    const form = section.querySelector("[data-golorama-form]");
    const submit = section.querySelector("[data-golorama-submit]");
    const results = section.querySelector("[data-golorama-results]");
    const pool = section.querySelector("[data-golorama-pool]");
    if (!form || !submit || !results || !pool) return;

    // `header` est optionnel : son absence ne doit pas empêcher le rendu.
    const ui = {
      results: results,
      pool: pool,
      header: section.querySelector("[data-golorama-results-header]"),
    };

    clearLegacyState();
    loadAjaxCache();

    let revealed = false;
    let started = false;

    // Restaure l'état si l'internaute est déjà venu.
    const saved = readSavedState();
    if (saved) {
      applyState(form, saved);
      revealed = true;
      setSubmitEnabled(submit, true);
      showResults(ui, saved);
      ajaxGender(saved.genre).then(() => showResults(ui, saved));
    }

    form.addEventListener("change", (e) => {
      const state = readState(form);
      const ready = isComplete(state);

      setSubmitEnabled(submit, ready);

      // `applyState` coche sans émettre de `change` : seuls les clics
      // réels de l'internaute arrivent ici. Non envoyé (cookies pas encore
      // acceptés), le démarrage est retenté au clic suivant.
      if (!started) started = track("golorama_start");

      // Lancé dès le choix du genre, volontairement hors du garde `ready` :
      // les réponses arrivent pendant que l'internaute finit son parcours.
      if (e.target && e.target.name === "genre") {
        ajaxGender(state.genre).then(() => {
          if (revealed && isComplete(readState(form))) showResults(ui, readState(form));
        });
      }

      if (!ready) return;

      saveState(state);
      // Re-rend à chaud : un changement de genre remet à jour liens et
      // visuels sans que l'internaute resoumette le formulaire.
      if (revealed) showResults(ui, state);
    });

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const state = readState(form);
      if (!isComplete(state)) return;

      revealed = true;
      saveState(state);
      showResults(ui, state);
      track("golorama_complete");

      // Après showResults : le header vient d'être révélé, il est donc
      // mesurable et le scroll atteint la bonne position.
      if (ui.header && !ui.header.hidden) {
        ui.header.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      // Filet : si les réponses n'étaient pas là à temps, on rerend dès
      // qu'elles arrivent. Sans effet si le cache est chaud. Klaviyo part
      // après ce rendu final, qui tient compte de la disponibilité.
      ajaxGender(state.genre).then(() => {
        showResults(ui, state);
        trackKlaviyo(results);
      });
    });

    // Délégation : les cartes sont déplacées entre pool et grilles à
    // chaque rendu, un écouteur par carte serait à recâbler.
    results.addEventListener("click", (e) => {
      const card = e.target.closest("[data-color]");
      if (!card || card.closest("[data-golorama-pool]")) return;

      track("golorama_click");
    });
  }

  // ===================================================================
  // Disponibilité (ajax)
  // Une couleur vide pour le genre choisi n'est pas proposée. Une requête
  // par carte rendue vers collection.golorama-ajax, cachée en
  // sessionStorage.
  // ===================================================================

  function ajaxUrl(card, genre) {
    const href = card.dataset[`url${GENDER_SUFFIX[genre]}`];
    if (!href) return null;
    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.set("view", AJAX_VIEW);
      return url.toString();
    } catch (e) {
      return null;
    }
  }

  async function ajaxCard(card, genre) {
    const key = `${card.dataset.color}|${genre}`;
    if (AVAILABILITY.has(key)) return;

    const url = ajaxUrl(card, genre);
    if (!url) return;

    // Repli ouvert : sans un « 0 » explicite, la carte reste affichée.
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), AJAX_TIMEOUT_MS);
      const res = await fetch(url, { signal: controller.signal, credentials: "same-origin" });
      clearTimeout(timer);
      if (!res.ok) return;

      const body = (await res.text()).trim();
      if (!/^\d+$/.test(body)) return;

      AVAILABILITY.set(key, Number(body) > 0);
    } catch (e) {}
  }

  // Balayage à l'échelle du document : les cartes ont pu être déplacées du
  // pool vers les grilles de tier par un rendu précédent.
  function ajaxGender(genre) {
    if (genre === DEFAULT_GENDER) return Promise.resolve();

    const cards = [...document.querySelectorAll("[data-color]")];
    return Promise.all(cards.map((card) => ajaxCard(card, genre))).then(saveAjaxCache);
  }

  // Une couleur n'est écartée que sur un « zéro » confirmé.
  function isUnavailable(slug, genre) {
    return AVAILABILITY.get(`${slug}|${genre}`) === false;
  }

  function loadAjaxCache() {
    try {
      const raw = sessionStorage.getItem(AJAX_STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      for (const key of Object.keys(data)) {
        if (typeof data[key] === "boolean") AVAILABILITY.set(key, data[key]);
      }
    } catch (e) {}
  }

  function saveAjaxCache() {
    try {
      const out = {};
      for (const [key, value] of AVAILABILITY) out[key] = value;
      sessionStorage.setItem(AJAX_STORAGE_KEY, JSON.stringify(out));
    } catch (e) {}
  }

  // ===================================================================
  // Rendu
  // ===================================================================

  function showResults(ui, state) {
    const results = ui.results;
    const pool = ui.pool;

    const tiers = paletteFor(state);
    if (!tiers) return;

    // Repli au pool avant placement : `appendChild` déplaçant les nœuds,
    // une carte non replacée resterait sinon dans le tier du rendu
    // précédent.
    resetCards(results, pool);

    let total = 0;

    tiers.forEach((colors, index) => {
      const tier = results.querySelector(`[data-golorama-tier="${index + 1}"]`);
      const grid = results.querySelector(`[data-golorama-tier-grid="${index + 1}"]`);
      if (!tier || !grid) return;

      let placed = 0;
      for (const slug of colors) {
        // Collection absente côté Shopify : la carte n'a pas été rendue,
        // on l'omet silencieusement (le tier est simplement plus court).
        const card = pool.querySelector(`[data-color="${slug}"]`);
        if (!card) continue;

        // Couleur sans aucun produit du genre choisi : on ne la propose pas.
        if (isUnavailable(slug, state.genre)) continue;

        applyGender(card, state.genre);
        card.hidden = false;
        grid.appendChild(card);
        placed += 1;
      }

      // Un tier sans aucune carte disponible est masqué, titre compris.
      tier.hidden = placed === 0;
      total += placed;
    });

    // Le titre « vos couleurs » n'apparaît qu'avec au moins une carte :
    // aucune collection rendue, aucun titre qui surplombe le vide.
    if (ui.header) ui.header.hidden = total === 0;
  }

  function paletteFor(state) {
    const raw = PALETTES[`${state.yeux}|${state.cheveux}|${state.peau}`];
    if (!raw) return null;

    return raw.split("/").map((tier) => tier.trim().split(/\s+/).filter(Boolean));
  }

  function resetCards(results, pool) {
    for (const card of results.querySelectorAll("[data-color]")) {
      card.hidden = true;
      pool.appendChild(card);
    }
    for (let i = 1; i <= TIER_COUNT; i += 1) {
      const tier = results.querySelector(`[data-golorama-tier="${i}"]`);
      if (tier) tier.hidden = true;
    }
  }

  // Le serveur n'émet `data-img-femme` / `data-img-homme` que si la
  // collection porte le métachamp correspondant.
  function applyGender(card, genre) {
    const key = GENDER_SUFFIX[genre] || GENDER_SUFFIX[DEFAULT_GENDER];

    const url = card.dataset[`url${key}`] || card.dataset.urlNa;
    if (url) card.href = url;

    const img = card.querySelector("img");
    if (!img) return;

    // Repli systématique sur le neutre : sans lui, passer de Féminin
    // (métachamp présent) à Masculin (absent) garderait le visuel féminin.
    const src = card.dataset[`img${key}`] || card.dataset.imgNa;
    const srcset = card.dataset[`srcset${key}`] || card.dataset.srcsetNa;
    const alt = card.dataset[`alt${key}`] || card.dataset.altNa;

    if (src) img.src = src;
    img.srcset = srcset || "";
    if (alt) img.alt = alt;
  }

  // ===================================================================
  // État
  // ===================================================================

  function readState(form) {
    return {
      yeux: checkedValue(form, "yeux"),
      cheveux: checkedValue(form, "cheveux"),
      peau: checkedValue(form, "peau"),
      genre: checkedValue(form, "genre") || DEFAULT_GENDER,
    };
  }

  // Le genre est optionnel, les trois autres critères non.
  function isComplete(state) {
    return Boolean(state.yeux && state.cheveux && state.peau);
  }

  function applyState(form, state) {
    check(form, "yeux", state.yeux);
    check(form, "cheveux", state.cheveux);
    check(form, "peau", state.peau);
    check(form, "genre", state.genre);
  }

  function readSavedState() {
    let raw = null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
    if (!raw) return null;

    let state = null;
    try {
      state = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!state || typeof state !== "object") return null;

    state.genre = state.genre || DEFAULT_GENDER;
    // Une palette introuvable signale un état écrit par une version
    // antérieure du vocabulaire : on repart de zéro.
    if (!isComplete(state) || !paletteFor(state)) return null;

    return state;
  }

  function saveState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function clearLegacyState() {
    try {
      for (const key of LEGACY_STORAGE_KEYS) localStorage.removeItem(key);
    } catch (e) {}
    document.cookie = `${LEGACY_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }

  // ===================================================================
  // Helpers
  // ===================================================================

  function checkedValue(form, name) {
    return form.querySelector(`input[name="${name}"]:checked`)?.value || "";
  }

  function check(form, name, value) {
    if (!value) return;
    const input = form.querySelector(`input[name="${name}"][value="${value}"]`);
    if (input) input.checked = true;
  }

  function setSubmitEnabled(submit, enabled) {
    submit.disabled = !enabled;
    submit.setAttribute("aria-disabled", String(!enabled));
  }
})();
